import { useState, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { useHotkeys } from 'react-hotkeys-hook'
import debounce from 'lodash/debounce'

import IconLink from 'components/Shared/IconLink/Index'
import SearchInput from 'components/Shared/SearchInput/Index'

const SEARCH_HIDE_TIMEOUT = 3000 // in ms

const StyledSearch = styled.div`
  display: inline-flex;
  height: 24px;
  justify-content: flex-end;
  align-items: center;
  position: relative;
`

const StyledSearchActivator = styled.div`
  display: inline-flex;
  align-items: center;
  width: 35px;
  height: 24px;
`

const StyledSearchButton = styled(IconLink)`
  position: absolute;
  right: 4px;
  padding: 0 4px;
`

const Search = ({ initialValue, onChange, onInputChange, placeholder, alwaysDisplay, withHotKeys, renderDropdown, inputRef }) => {
  const [inputValue, setInputValue] = useState(initialValue || '')
  const [searchShow, setSearchShow] = useState(false)
  const [inputWasMade, setInputWasMade] = useState(false)

  useHotkeys('/', (e) => {
    e.preventDefault()
    setInputWasMade(true)
  }, {
    useKey: true,
    enabled: !!withHotKeys,
  })

  const onSearch = () => { onChange(inputValue) }
  const onClear = () => {
    setInputValue('')
    onInputChange?.('')
    if (initialValue) {
      onChange('')
    }
  }

  const onInput = (event) => {
    setInputWasMade(true)
    setInputValue(event.target.value)
    onInputChange?.(event.target.value)
  }

  const onKeyPress = (event) => {
    if (event.key === 'Enter') {
      onChange(inputValue)
    }
  }

  const onKeyDown = (event) => {
    if (event.key === 'Escape') {
      setSearchShow(false)
      setInputValue('')
      setInputWasMade(false)
      onInputChange?.('')
    }
  }

  const showSearch = () => { setSearchShow(true) }
  const searchHide = () => { setSearchShow(false) }
  const debouncedHideRef = useRef(debounce(searchHide, SEARCH_HIDE_TIMEOUT))
  const hideSearchWithDelay = debouncedHideRef.current

  // cancel debounced async function to prevent warning after component is destroyed
  useEffect(() => {
    return () => {
      hideSearchWithDelay.cancel()
    }
  }, [hideSearchWithDelay])

  const hideUnfocusedSearch = () => { setInputWasMade(false) }

  const showInput = Boolean(alwaysDisplay || searchShow || inputValue || inputWasMade)

  let searchContent

  if (showInput) {
    searchContent = (
      <>
        <SearchInput
          inputRef={inputRef}
          autoFocus
          autoComplete
          value={inputValue}
          onChange={onInput}
          onClear={onClear}
          onSearch={onSearch}
          onKeyPress={onKeyPress}
          placeholder={placeholder}
          onKeyDown={onKeyDown}
          withHotKeys={withHotKeys}
        />
        {renderDropdown?.({ inputValue, isVisible: showInput })}
      </>
    )
  } else {
    searchContent = (
      <StyledSearchActivator>
        <StyledSearchButton
          iconName='icon-search'
          onClick={onSearch}
        />
      </StyledSearchActivator>
    )
  }

  return (
    <StyledSearch
      onMouseEnter={showSearch}
      onMouseLeave={hideSearchWithDelay}
      onBlur={hideUnfocusedSearch}
    >
      {searchContent}
    </StyledSearch>
  )
}


Search.propTypes = {
  initialValue: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onInputChange: PropTypes.func,
  placeholder: PropTypes.string,
  alwaysDisplay: PropTypes.bool,
  withHotKeys: PropTypes.bool,
  renderDropdown: PropTypes.func,
  inputRef: PropTypes.object,
}

export default Search
